import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subscription } from "rxjs/Subscription";
import { Errors } from "../../models";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Location } from "@angular/common";
import {
  ConfirmationService,
  ModalService,
  PatientService,
  UserService,
  ValidateService,
} from "../../services";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-pop-up-add-medicine",
  templateUrl: "./pop-up-add-medicine.component.html",
  styleUrls: ["./pop-up-add-medicine.component.scss"],
})
export class PopUpAddMedicineComponent implements OnInit, OnDestroy {
  private subscription: Subscription;
  public user;
  public edit: boolean;
  private medicine;
  public countMedicines;
  public gotDuplicatedHours: boolean;
  errors: Errors = { errors: {} };
  isSubmitting = false;
  addMedicineForm: FormGroup;
  @Input() idMedicine!: number;
  @Input() imei!: number;

  constructor(
    private fb: FormBuilder,
    private location: Location,
    private validateService: ValidateService,
    private route: ActivatedRoute,
    private router: Router,
    private patientService: PatientService,
    private userService: UserService,
    private modalService: ModalService,
    private confirmation: ConfirmationService,
    private translateService: TranslateService,
    public activeModal: NgbActiveModal,
  ) {}

  ngOnInit() {
    this.addMedicineForm = this.fb.group({
      name: [
        "",
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(19),
          this.validateService.checkAlphanumeric,
        ],
      ],
      description: [
        "",
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(512),
          Validators.pattern(/^((?!\s{2,}).)*$/),
          this.validateService.checkAlphanumeric,
        ],
      ],
      takeHours: this.fb.array([], this.validateService.checkHoursDuplicate),
    });

    this.addRequiredHour();

    this.idMedicine != undefined ? (this.edit = true) : (this.edit = false);

    this.patientService.getMedicinesSchedule(this.imei).subscribe((data) => {
      this.countMedicines = data.data.length;
      if (this.countMedicines >= 10 && !this.edit) {
        this.modalService.open(
          this.translateService.instant(
            "BACKEND.MAXIMUM-COUNT-MEDICINES-REACHED",
          ),
          undefined,
          undefined,
          'EVENTS.SHARED.COMPONENTS.POP-UP-ADD-MEDICINE.POPUP.BACKEND-MAXIMUM-COUNT-MEDICINES-REACHED',
        );
        this.goBack();
      } else {
        if (this.edit) {
          this.patientService.getMedicine(this.idMedicine).subscribe(
            (data) => {
              this.medicine = data.data;

              this.medicine.take_hours = this.medicine.take_hours.filter(
                (hour) => hour && hour.hour !== undefined && hour.hour !== "",
              );

              for (let i = 0; i < this.medicine.take_hours.length - 1; i++) {
                this.addHour(true);
              }

              this.addMedicineForm.patchValue({
                name: this.medicine.name,
                description: this.medicine.description,
                takeHours: this.medicine.take_hours,
              });
            },
            // () => this.modalService.open(this.translateService.instant('BACKEND.COULDNT-GET-DATA'))
          );
        }
      }
      this.patientService.getPatient(this.imei).subscribe(
        (data) => {
          this.user = data;
        },
        // () => this.modalService.open(this.translateService.instant('BACKEND.COULDNT-GET-DATA'))
      );
    });
  }

  get hours() {
    return <FormArray>this.addMedicineForm.get("takeHours");
  }

  private addRequiredHour(): void {
    const hour = new FormGroup({
      hour: new FormControl("", [
        Validators.required,
        this.validateService.checkHourFormat,
      ]),
    });

    this.hours.push(hour);
  }

  public addHour(addFromApi = false): void {
    const lastHour = this.hours.at(this.hours.length - 1).value.hour;
    if (addFromApi || (lastHour !== null && lastHour !== "")) {
      const hour = new FormGroup({
        hour: new FormControl("", [
          Validators.required,
          this.validateService.checkHourFormat,
        ]),
      });

      this.hours.push(hour);
    } else {
      this.modalService.open(
        this.translateService.instant("BACKEND.ADD-PREVIOUS-HOUR"),
        undefined,
        undefined,
        'EVENTS.SHARED.COMPONENTS.POP-UP-ADD-MEDICINE.POPUP.BACKEND-ADD-PREVIOUS-HOUR',
      );
    }
  }

  public deleteHour(index): void {
    if (index >= 0 && this.hours.length > 1) {
      (<FormArray>this.addMedicineForm.get("takeHours")).removeAt(index);
    }
  }

  public checkDuplicatedHours(): void {
    // blokada podwójnego kliknięcia - żądanie już leci
    if (this.isSubmitting) {
      return;
    }

    this.gotDuplicatedHours = false;

    const sorted_data = this.addMedicineForm
      .get("takeHours")
      .value.slice(0)
      .sort((a, b) => {
        if (a.hour < b.hour) {
          return 1;
        }
        if (a.hour > b.hour) {
          return -1;
        }
      });

    for (let i = 0; i < sorted_data.length - 1; i++) {
      if (sorted_data[i].hour !== null && sorted_data[i].hour !== "") {
        if (sorted_data[i + 1].hour === sorted_data[i].hour) {
          this.gotDuplicatedHours = true;
        }
      }
    }
    this.validateService.validateAllFormFields(this.addMedicineForm);
    if (this.gotDuplicatedHours) {
      this.modalService.open(
        this.translateService.instant("FORMS.HOUR-DUPLICATE"),
        undefined,
        undefined,
        'EVENTS.SHARED.COMPONENTS.POP-UP-ADD-MEDICINE.POPUP.FORMS-HOUR-DUPLICATE',
      );
    } else {
      this.submitForm();
    }
  }

  public submitForm(): void {
    if (this.isSubmitting) {
      return;
    }

    if (this.addMedicineForm.valid) {
      this.isSubmitting = true;

      this.errors = { errors: {} };
      let credentials = this.addMedicineForm.value;

      credentials.takeHours = credentials.takeHours.filter(
        (hour) => hour.hour != null && hour.hour !== "",
      );
      credentials.band = this.imei;

      if (this.edit) {
        this.patientService
          .editMedicine(this.medicine.id, credentials)
          .subscribe(
            () => {
              this.confirmation.showAndNavigate(
                "BACKEND.EDITED_MEDICINE",
                "/users/user/" + this.imei,
                { name: this.addMedicineForm.get("name").value },
                'EVENTS.SHARED.COMPONENTS.POP-UP-ADD-MEDICINE.POPUP.BACKEND-EDITED_MEDICINE'
              );
              this.activeModal.close('Ok click');
            },
            (err) => {
              this.errors = err;
              this.isSubmitting = false;
            },
            () => (this.isSubmitting = false),
          );
      } else {
        this.patientService.addMedicine(credentials).subscribe(
          () => {
            this.confirmation.showAndNavigate(
              "BACKEND.ADDED_MEDICINE",
              "/users/user/" + this.imei,
              { name: this.addMedicineForm.get("name").value },
              'EVENTS.SHARED.COMPONENTS.POP-UP-ADD-MEDICINE.POPUP.BACKEND-ADDED_MEDICINE'
            );
            this.activeModal.close('Ok click');
          },
          (err) => {
            this.errors = err;
            this.isSubmitting = false;
          },
          () => (this.isSubmitting = false),
        );
      }
    } else {
      this.validateService.validateAllFormFields(this.addMedicineForm);
    }
  }

  public goBack(): void {
    this.activeModal.close("Ok click");
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public deleteMedicine() {
    this.modalService.openAccept(this.translateService.instant('BACKEND.ARE-YOU-SURE-MEDICINE'), 'EVENTS.SHARED.COMPONENTS.POP-UP-ADD-MEDICINE.POPUP.BACKEND-ARE-YOU-SURE-MEDICINE')
      .subscribe(
        data => {
          if (data.ok) {
            const deleteAction = this.patientService.deleteMedicine(this.idMedicine);

            deleteAction.subscribe(
              () => {
                this.confirmation.show('BACKEND.DELETED_MEDICINE', undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-ADD-MEDICINE.POPUP.BACKEND-DELETED_MEDICINE');
                this.goBack();
              },
              () => {
                this.modalService.open(this.translateService.instant('BACKEND.COULDNT-DELETE'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.POP-UP-ADD-MEDICINE.POPUP.BACKEND-COULDNT-DELETE');
              }
            );
          }});
  }

}
